import { MODE_KEYMAP, MAP_MODES, STAR_MAPPING_URL } from '../constants.js';
import { escapeHtml } from '../helpers.js';

/**
 * Add mode-management helpers (normal/star/edit) and entity selection logic.
 * @param {typeof import('../app.js').default} App
 */
export function applyModeMixins(App) {
	/**
	 * Handle clicks/selection on cities or nations.
	 * @param {'city'|'nation'} kind
	 * @param {object} entity
	 * @param {object} [event]
	 */
	App.prototype.handleEntitySelection = function handleEntitySelection(
		kind,
		entity,
		event
	) {
		if (!entity) {
		 return;
		}
		const normalizedKind = kind === 'nation' ? 'nation' : 'city';
		this.lastLoreSelection = { kind: normalizedKind, entity };
		if (this.isEditMode()) {
			const latlng =
				event && event.latlng
					? event.latlng
					: this.getEntityLatLng(normalizedKind, entity);
			this.showDebugInfo(normalizedKind, entity, { latlng });
			return;
		}
		this.showEntityInfo(normalizedKind, entity);
	};

	/**
	 * Load and display the lore panel for the given entity.
	 * @param {'city'|'nation'} kind
	 * @param {object} entity
	 */
	App.prototype.showEntityInfo = function showEntityInfo(kind, entity) {
		if (!this.lorePanelEl || !entity) {
			return;
		}

		if (kind === 'city') {
			this.ensureCityEntry(entity);
		}

		this.activatePanel('lore');
		this.expandPanel();
		this.highlightEntity(kind, entity);
		this.lastLoreSelection = { kind, entity };

		const infoId = this.getInfoId(entity);
		if (!infoId) {
			this.setPanelMessage(
				entity.name,
				'<p class="info-panel__placeholder">No lore available yet.</p>'
			);
			return;
		}

		this.updateAnchor(kind, infoId);

		const markdownPath = this.getMarkdownPath(kind, infoId);
		this.setPanelMessage(
			entity.name,
			'<p class="info-panel__placeholder">Loading lore...</p>'
		);

		const cached = this.infoCache[markdownPath];
		if (cached) {
			const cachedHtml = typeof cached === 'string' ? cached : cached.html;
			this.setPanelContent(entity.name, cachedHtml);
			return;
		}

		fetch(markdownPath)
			.then((response) => {
				if (!response.ok) {
					throw new Error(`Lore not found: ${markdownPath}`);
				}
				return response.text();
			})
			.then((markdown) =>
				this.ensureMarkdownRenderer()
					.catch((loaderError) => {
						console.warn(loaderError);
						return null;
					})
					.then(() => {
						const html = this.renderMarkdown(markdown);
						this.infoCache[markdownPath] = { html };
						this.setPanelContent(entity.name, html);
					})
			)
			.catch((error) => {
				console.warn(error);
				this.setPanelMessage(
					entity.name,
					'<p class="info-panel__placeholder">Lore has not been uploaded for this location yet.</p>'
				);
			});
	};

	/**
	 * Render edit-mode debugging info for a city or nation.
	 * @param {'city'|'nation'} kind
	 * @param {object} entity
	 * @param {{latlng?:{lat:number,lng:number}}} [options]
	 */
	App.prototype.showDebugInfo = function showDebugInfo(kind, entity, options) {
		if (!this.lorePanelEl || !entity) {
			return;
		}
		const normalizedKind = kind === 'nation' ? 'nation' : 'city';
		this.activatePanel('lore');
		this.expandPanel();
		this.highlightEntity(normalizedKind, entity);
		this.lastDebugSelection = { kind: normalizedKind, entity };
		if (!this.starMappingData && typeof this.loadStarData === 'function') {
			this.loadStarData();
		}

		const infoId = this.getInfoId(entity);
		const latlng =
			options && options.latlng
				? options.latlng
				: this.getEntityLatLng(normalizedKind, entity);
		if (latlng) {
			this.updateStarMappingCoordinates(normalizedKind, entity, latlng.lat, latlng.lng);
		}
		const displayTitle = entity && entity.name ? `${entity.name} (edit)` : 'Edit mode';
		const jsonEntry =
			normalizedKind === 'city'
				? this.getStarMappingCityEntry(entity)
				: this.getStarMappingNationEntry(entity);
		if (jsonEntry) {
			const jsonString = this.stringifyEditJson(jsonEntry);
			const html = this.renderJsonEditPanel(normalizedKind, entity, infoId, jsonString);
			this.setPanelContent(displayTitle, html);
			return;
		}
		const fallbackHtml = this.renderLegacyDebugPanel(normalizedKind, entity, infoId, latlng);
		this.setPanelContent('Edit mode', fallbackHtml);
	};

	/**
	 * Serialize an entity record for display in edit mode.
	 * @param {object} entry
	 * @returns {string}
	 */
	App.prototype.stringifyEditJson = function stringifyEditJson(entry) {
		if (!entry) {
			return '';
		}
		try {
			return JSON.stringify(entry, null, 2);
		} catch (_error) {
			return '{ "error": "Unable to serialize entry." }';
		}
	};

	/**
	 * Build the HTML for the edit-mode JSON/debug panel.
	 * @param {'city'|'nation'} kind
	 * @param {object} entity
	 * @param {string} infoId
	 * @param {string} jsonString
	 * @returns {string}
	 */
	App.prototype.renderJsonEditPanel = function renderJsonEditPanel(kind, entity, infoId, jsonString) {
		const rows = [];
		if (infoId) {
			rows.push(this.createDebugRow('Slug', infoId));
		}
		if (kind === 'city' && entity && entity.nation) {
			rows.push(this.createDebugRow('Nation', entity.nation));
		}
		if (kind === 'nation' && entity && entity.boundary) {
			rows.push(this.createDebugRow('Boundary', entity.boundary));
		}
		const metaList = rows.length
			? '<dl class="debug-list debug-list--compact">' + rows.join('') + '</dl>'
			: '';
	const label = kind === 'city' ? 'City JSON' : 'Nation JSON';
	const note =
		`<p class="info-panel__note"><strong>Edit mode</strong> is active. Drag markers to adjust coordinates. The JSON below mirrors <code>${escapeHtml(STAR_MAPPING_URL)}</code> so you can copy updates directly back into your source. Press <kbd>N</kbd> to return to normal mode.</p>`;
	const sections = [];
	sections.push(
		this.renderJsonBlock(label, jsonString, 'primary', {
			emptyMessage: 'Data unavailable.'
		})
	);
	if (kind === 'nation' && entity && entity.boundary) {
		sections.push(
			this.renderJsonBlock('Boundary GeoJSON', this.getBoundaryJsonString(entity), 'boundary', {
				elementId: 'boundary_geojson_preview',
				boundarySlug: entity.boundary,
				emptyMessage: 'Boundary data loading...'
			})
		);
	}
	return note + metaList + sections.join('');
	};

	/**
	 * Render a copyable JSON block for the debug panel.
	 * @param {string} label
	 * @param {string} jsonString
	 * @param {string} payloadId
	 * @param {object} [options]
	 * @returns {string}
	 */
	App.prototype.renderJsonBlock = function renderJsonBlock(label, jsonString, payloadId, options) {
		const settings = options || {};
		const resolvedLabel = escapeHtml(label);
		const normalizedKey = typeof payloadId === 'string' && payloadId ? payloadId : 'primary';
		const payloadKey = normalizedKey.replace(/[^a-z0-9_-]/gi, '') || 'primary';
		const hasJson = typeof jsonString === 'string' && jsonString.trim().length > 0;
		const buttonAttrs = [
			'class="json-copy-button"',
			'type="button"',
			'aria-label="Copy JSON"',
			'data-action="copy-json"',
			`data-json-source="${payloadKey}"`,
			`data-json-ready="${hasJson ? 'true' : 'false'}"`
		];
		if (!hasJson) {
			buttonAttrs.push('disabled');
			buttonAttrs.push('aria-disabled="true"');
		}
		const preAttrs = ['class="edit-json__preview"'];
		if (settings.elementId) {
			preAttrs.push(`id="${settings.elementId}"`);
		}
		if (settings.boundarySlug) {
			preAttrs.push(`data-boundary-slug="${escapeHtml(settings.boundarySlug)}"`);
		}
		const codeAttrs = [`data-json-payload="${payloadKey}"`];
		const codeContent = hasJson ? escapeHtml(jsonString) : '';
		let extras = '';
		if (!hasJson) {
			const placeholderText = settings.emptyMessage || 'Data not available.';
			extras =
				`<span class="edit-json__placeholder" data-json-placeholder-for="${payloadKey}">` +
				escapeHtml(placeholderText) +
				'</span>';
		}
		return (
			'<div class="edit-json">' +
			'<div class="edit-json__toolbar">' +
			'<span class="edit-json__label">' +
			resolvedLabel +
			'</span>' +
			'<button ' +
			buttonAttrs.join(' ') +
			'>Copy JSON</button>' +
			'</div>' +
			`<pre ${preAttrs.join(' ')}>` +
			`<code ${codeAttrs.join(' ')}>` +
			codeContent +
			'</code>' +
			extras +
			'</pre>' +
			'</div>'
		);
	};

/**
 * Retrieve cached boundary geometry for the given nation.
 * @param {object} entity
 * @returns {string|null}
 */
App.prototype.getBoundaryJsonString = function getBoundaryJsonString(entity) {
		if (!entity || !entity.boundary) {
			return null;
		}
		const geometry = this.getBoundaryGeometry(entity.boundary);
		if (!geometry) {
			return null;
		}
		return this.stringifyEditJson(geometry);
	};

/**
 * Render the older debug panel when JSON data is unavailable.
 */
App.prototype.renderLegacyDebugPanel = function renderLegacyDebugPanel(kind, entity, infoId, latlng) {
		const latLabel = latlng ? this.formatCoordinate(latlng.lat) : '—';
		const lonLabel = latlng ? this.formatCoordinate(latlng.lng) : '—';
		const rows = [];
		rows.push(this.createDebugRow('Name', entity.name || 'Unnamed'));
		rows.push(this.createDebugRow('Type', kind === 'city' ? 'City' : 'Nation'));
		rows.push(this.createDebugRow('Slug', infoId || '—'));
		rows.push(this.createDebugRow('Latitude', latLabel));
		rows.push(this.createDebugRow('Longitude', lonLabel));
		if (infoId) {
			rows.push(
				this.createDebugRow('Markdown path', this.getMarkdownPath(kind, infoId))
			);
		}
		if (kind === 'nation' && entity.boundary) {
			rows.push(this.createDebugRow('Boundary', entity.boundary));
		}
		if (kind === 'city' && entity.align) {
			rows.push(this.createDebugRow('Alignment', entity.align));
		}
		let rawEntry = '';
		try {
			rawEntry = JSON.stringify(entity, null, 2);
		} catch (_error) {
			rawEntry = '{ "error": "Unable to serialize entity." }';
		}
		const boundaryDetails = this.renderBoundaryDetails(kind, entity);
		return (
			'<p class="info-panel__note"><strong>Edit mode</strong> is active. Drag markers to adjust coordinates and copy the updated values below. Press <kbd>N</kbd> to return to normal mode.</p>' +
			'<dl class="debug-list">' +
			rows.join('') +
			'</dl>' +
			boundaryDetails +
			'<details class="debug-list__details"><summary>Raw entry</summary><pre>' +
			escapeHtml(rawEntry) +
			'</pre></details>'
		);
	};

/**
 * Look up a nation entry inside star-mapping data by slug.
 */
App.prototype.getStarMappingNationEntryBySlug = function getStarMappingNationEntryBySlug(slug) {
		if (!this.starMappingData || !slug) {
			return null;
		}
		const normalized = typeof slug === 'string' ? slug.trim() : '';
		if (!normalized) {
			return null;
		}
		return this.starMappingData[normalized] || null;
	};

	App.prototype.getStarMappingNationEntry = function getStarMappingNationEntry(entity) {
		if (!entity) {
			return null;
		}
		const slug = entity.slug || this.getInfoId(entity);
		if (!slug) {
			return null;
		}
		return this.getStarMappingNationEntryBySlug(slug);
	};

	App.prototype.getStarMappingCityEntry = function getStarMappingCityEntry(city) {
		if (!city || !this.starMappingData) {
			return null;
		}
		const nationSlug = city.nation || city.nationSlug;
		const citySlug = city.slug || this.getInfoId(city);
		if (!nationSlug || !citySlug) {
			return null;
		}
		const nationEntry = this.getStarMappingNationEntryBySlug(nationSlug);
		if (!nationEntry || !Array.isArray(nationEntry.cities)) {
			return null;
		}
		for (let i = 0; i < nationEntry.cities.length; i += 1) {
			const candidate = nationEntry.cities[i];
			if (!candidate) {
				continue;
			}
			if (candidate.slug === citySlug) {
				return candidate;
			}
			if (!candidate.slug && candidate.name && candidate.name === city.name) {
				return candidate;
			}
		}
		return null;
	};

	App.prototype.updateStarMappingCoordinates = function updateStarMappingCoordinates(kind, entity, lat, lon) {
		if (!this.starMappingData) {
			return;
		}
		if (typeof lat !== 'number' || typeof lon !== 'number') {
			return;
		}
		if (kind === 'city') {
			const cityEntry = this.getStarMappingCityEntry(entity);
			if (!cityEntry) {
				return;
			}
			if (!cityEntry.coordinates) {
				cityEntry.coordinates = {};
			}
			cityEntry.coordinates.lat = lat;
			cityEntry.coordinates.lon = lon;
			return;
		}
		const nationEntry = this.getStarMappingNationEntry(entity);
		if (!nationEntry) {
			return;
		}
		if (!nationEntry.coordinates) {
			nationEntry.coordinates = {};
		}
		nationEntry.coordinates.lat = lat;
		nationEntry.coordinates.lon = lon;
	};

	App.prototype.createDebugRow = function createDebugRow(label, value) {
		return (
			'<dt>' +
			escapeHtml(label) +
			'</dt><dd>' +
			escapeHtml(value == null ? '' : String(value)) +
			'</dd>'
		);
	};

	App.prototype.formatCoordinate = function formatCoordinate(value) {
		if (typeof value !== 'number' || !isFinite(value)) {
			return '—';
		}
		return value.toFixed(5);
	};

	App.prototype.renderBoundaryDetails = function renderBoundaryDetails(kind, entity) {
		if (kind !== 'nation' || !entity || !entity.boundary) {
			return '';
		}
		const geometry = this.getBoundaryGeometry(entity.boundary);
		const formatted = geometry
			? JSON.stringify(geometry, null, 2)
			: 'Boundary data loading...';
		return (
			'<details class="debug-list__details"><summary>Boundary GeoJSON</summary><pre id="boundary_geojson_preview" data-boundary-slug="' +
			escapeHtml(entity.boundary) +
			'">' +
			escapeHtml(formatted) +
			'</pre></details>'
		);
	};

	App.prototype.getBoundaryGeometry = function getBoundaryGeometry(boundarySlug) {
		if (!boundarySlug) {
			return null;
		}
		return this.boundaryCache[boundarySlug] || null;
	};

	App.prototype.getEntityLatLng = function getEntityLatLng(kind, entity) {
		if (!entity) {
			return null;
		}
		if (typeof entity.lat === 'number' && typeof entity.lon === 'number') {
			return { lat: entity.lat, lng: entity.lon };
		}
		let entry = null;
		if (kind === 'nation') {
			if (this.countryEntryMap && this.countryEntryMap.get(entity)) {
				entry = this.countryEntryMap.get(entity);
			} else {
				for (let i = 0; i < this.countryMarkers.length; i += 1) {
					if (this.countryMarkers[i] && this.countryMarkers[i].data === entity) {
						entry = this.countryMarkers[i];
						break;
					}
				}
			}
		} else {
			entry = this.getCityEntry(entity);
		}
		if (entry && entry.marker && typeof entry.marker.getLatLng === 'function') {
			const markerLatLng = entry.marker.getLatLng();
			if (markerLatLng) {
				return { lat: markerLatLng.lat, lng: markerLatLng.lng };
			}
		}
		return null;
	};

	App.prototype.handleCityDragEnd = function handleCityDragEnd(entry, event) {
		if (!entry || !entry.marker || !entry.data) {
			return;
		}
		const latLng =
			(event && event.latlng) ? event.latlng : entry.marker.getLatLng();
		if (!latLng) {
			return;
		}
		entry.data.lat = latLng.lat;
		entry.data.lon = latLng.lng;
		this.updateStarMappingCoordinates('city', entry.data, latLng.lat, latLng.lng);
		const cityKeys = [entry.data.name, entry.data.slug];
		this.updateCityCoordinateIndex(cityKeys, latLng.lat, latLng.lng);
		this.updateStarLinesForCity(cityKeys);
		if (typeof this.syncCityWrapMarkers === 'function') {
			this.syncCityWrapMarkers(entry);
		}
		if (this.isEditMode()) {
			this.showDebugInfo('city', entry.data, { latlng: latLng });
		}
	};

	App.prototype.handleCountryDragEnd = function handleCountryDragEnd(entry, event) {
		if (!entry || !entry.marker || !entry.data) {
			return;
		}
		const latLng =
			(event && event.latlng) ? event.latlng : entry.marker.getLatLng();
		if (!latLng) {
			return;
		}
		entry.data.lat = latLng.lat;
		entry.data.lon = latLng.lng;
		this.updateStarMappingCoordinates('nation', entry.data, latLng.lat, latLng.lng);
		if (entry.labelEl) {
			entry.lastFontSize = null;
		}
		if (typeof this.syncCountryWrapMarkers === 'function') {
			this.syncCountryWrapMarkers(entry);
		}
		if (this.isEditMode()) {
			this.showDebugInfo('nation', entry.data, { latlng: latLng });
		}
	};

	App.prototype.setCityMarkerDraggable = function setCityMarkerDraggable(entry, shouldEnable) {
		if (!entry || !entry.marker || !entry.marker.dragging) {
			return;
		}
		if (shouldEnable) {
			entry.marker.dragging.enable();
		} else {
			entry.marker.dragging.disable();
		}
	};

	App.prototype.setCountryMarkerDraggable =
		function setCountryMarkerDraggable(entry, shouldEnable) {
			if (!entry || !entry.marker || !entry.marker.dragging) {
				return;
			}
			if (shouldEnable) {
				entry.marker.dragging.enable();
			} else {
				entry.marker.dragging.disable();
			}
		};

	App.prototype.initializeModeManager = function initializeModeManager() {
		this.createStarModeToggle();
		if (typeof document !== 'undefined') {
			document.addEventListener('keydown', (event) => this.onModeKeydown(event), false);
		}
		this.updateModeAttributes();
		this.applyModeToCountryMarkers();
		this.applyModeToCityMarkers();
		this.refreshBoundaryEditHandles();
		this.updateStarModeToggleState();
	};

	App.prototype.onModeKeydown = function onModeKeydown(event) {
		if (!event || typeof event.key !== 'string') {
			return;
		}
		if (event.ctrlKey || event.metaKey || event.altKey) {
			return;
		}
		const target = event.target;
		if (target) {
			const tagName = target.tagName ? target.tagName.toLowerCase() : '';
			if (
				tagName === 'input' ||
				tagName === 'textarea' ||
				tagName === 'select' ||
				target.isContentEditable
			) {
				return;
			}
		}
		const key = event.key.toLowerCase();
		const mode = MODE_KEYMAP[key];
		if (!mode) {
			return;
		}
		if (mode === MAP_MODES.STAR && this.activeMode === MAP_MODES.STAR) {
			this.setActiveMode(MAP_MODES.NORMAL);
			event.preventDefault();
			return;
		}
		if (mode !== this.activeMode) {
			this.setActiveMode(mode);
			event.preventDefault();
		}
	};

	App.prototype.setActiveMode = function setActiveMode(mode) {
		const allowed = mode === MAP_MODES.NORMAL || mode === MAP_MODES.STAR || mode === MAP_MODES.EDIT;
		if (!allowed) {
			return;
		}
		if (mode === this.activeMode) {
			return;
		}
		const previousMode = this.activeMode;
		this.activeMode = mode;
		this.updateModeAttributes();
		this.applyModeToCityMarkers();
		if (typeof this.applyModeToSyntheticMarkers === 'function') {
			this.applyModeToSyntheticMarkers();
		}
		this.applyModeToCountryMarkers();
		const enteringStar = mode === MAP_MODES.STAR;
		const leavingStar = previousMode === MAP_MODES.STAR;
		if (enteringStar) {
			this.revealStarModeToggle();
			this.enableStarMode();
		} else if (leavingStar) {
			this.disableStarMode();
		}
		if (mode === MAP_MODES.EDIT) {
			this.refreshBoundaryEditHandles();
			if (this.lastDebugSelection && this.lastDebugSelection.entity) {
				this.showDebugInfo(this.lastDebugSelection.kind, this.lastDebugSelection.entity);
			} else {
				this.showModeIntro();
			}
			this.updateStarModeToggleState();
			return;
		}
		if (previousMode === MAP_MODES.EDIT) {
			this.teardownBoundaryEditHandles();
			if (this.lastLoreSelection && this.lastLoreSelection.entity) {
				this.showEntityInfo(this.lastLoreSelection.kind, this.lastLoreSelection.entity);
			} else {
				this.showPlaceholder();
			}
		}
		this.updateStarModeToggleState();
	};

	App.prototype.isEditMode = function isEditMode() {
		return this.activeMode === MAP_MODES.EDIT;
	};

	App.prototype.isStarMode = function isStarMode() {
		return this.activeMode === MAP_MODES.STAR;
	};

	App.prototype.applyModeToCityMarkers = function applyModeToCityMarkers() {
		const draggable = this.isEditMode();
		for (let i = 0; i < this.cityMarkers.length; i += 1) {
			this.setCityMarkerDraggable(this.cityMarkers[i], draggable);
		}
	};

	App.prototype.applyModeToCountryMarkers = function applyModeToCountryMarkers() {
		const draggable = this.isEditMode();
		for (let i = 0; i < this.countryMarkers.length; i += 1) {
			this.setCountryMarkerDraggable(this.countryMarkers[i], draggable);
		}
	};

	App.prototype.updateModeAttributes = function updateModeAttributes() {
		if (typeof document !== 'undefined') {
			const root = document.documentElement || document.body;
			if (root) {
				root.setAttribute('data-map-mode', this.activeMode);
			}
		}
		if (this.map && typeof this.map.getContainer === 'function') {
			const container = this.map.getContainer();
			if (container) {
				container.setAttribute('data-map-mode', this.activeMode);
			}
		}
	};

	App.prototype.createStarModeToggle = function createStarModeToggle() {
		if (!this.map || !globalThis.L || this.starModeControl) {
			return;
		}
		const control = globalThis.L.control({ position: 'topleft' });
		control.onAdd = () => {
			const container = globalThis.L.DomUtil.create(
				'div',
				'leaflet-control map-mode-toggle'
			);
			const actions = globalThis.L.DomUtil.create(
				'div',
				'map-mode-toggle__actions',
				container
			);
			this.starModeContainer = container;
			if (!this.starModeControlRevealed) {
				container.classList.add('is-hidden');
			}

			const homeLink = globalThis.L.DomUtil.create(
				'a',
				'map-mode-toggle__home',
				actions
			);
			homeLink.href = '/';
			homeLink.setAttribute('aria-label', 'Back to The Hierophant');
			homeLink.setAttribute('title', 'Back to The Hierophant');
			const homeIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
			homeIcon.setAttribute('class', 'map-mode-toggle__home-icon');
			homeIcon.setAttribute('viewBox', '0 0 24 24');
			homeIcon.setAttribute('aria-hidden', 'true');
			const homePath = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'path'
			);
			homePath.setAttribute(
				'd',
				'M12 3.3 3.9 9.5c-.3.2-.4.5-.4.8 0 .4.3.7.7.7h1.2v6.3c0 .9.7 1.7 1.7 1.7h9c.9 0 1.7-.7 1.7-1.7V11h1.2c.4 0 .7-.3.7-.7 0-.3-.1-.6-.4-.8L12 3.3Zm0 2.1 5 3.7V17c0 .1-.1.2-.2.2H7.2c-.1 0-.2-.1-.2-.2v-7.9l5-3.7Z'
			);
			homeIcon.appendChild(homePath);
			homeLink.appendChild(homeIcon);

			const button = globalThis.L.DomUtil.create(
				'button',
				'map-mode-toggle__button',
				actions
			);
			button.type = 'button';
			button.setAttribute('aria-pressed', 'false');
			button.setAttribute('aria-label', 'Enter Star mode');

			const title = globalThis.L.DomUtil.create(
				'span',
				'map-mode-toggle__title',
				button
			);
			title.textContent = 'Star mode';

			const status = globalThis.L.DomUtil.create(
				'span',
				'map-mode-toggle__status',
				button
			);
			status.textContent = 'Off';
			status.setAttribute('aria-hidden', 'true');

			const hint = globalThis.L.DomUtil.create(
				'span',
				'map-mode-toggle__hint',
				button
			);
			hint.textContent = 'S';
			hint.setAttribute('aria-hidden', 'true');

			button.addEventListener('click', (event) => {
				if (event && typeof event.preventDefault === 'function') {
					event.preventDefault();
				}
				this.toggleStarMode();
			});

			this.starModeButton = button;
			this.starModeStatusEl = status;

			if (globalThis.L && globalThis.L.DomEvent) {
				globalThis.L.DomEvent.disableClickPropagation(container);
				globalThis.L.DomEvent.disableScrollPropagation(container);
			}

			return container;
		};
		this.starModeControl = control;
		control.addTo(this.map);
		this.updateStarModeToggleState();
	};

	App.prototype.revealStarModeToggle = function revealStarModeToggle() {
		if (this.starModeControlRevealed) {
			return;
		}
		this.starModeControlRevealed = true;
		if (this.starModeContainer) {
			this.starModeContainer.classList.remove('is-hidden');
		}
	};

	App.prototype.toggleStarMode = function toggleStarMode() {
		if (this.activeMode === MAP_MODES.STAR) {
			this.setActiveMode(MAP_MODES.NORMAL);
			return;
		}
		this.setActiveMode(MAP_MODES.STAR);
	};

	App.prototype.updateStarModeToggleState = function updateStarModeToggleState() {
		if (!this.starModeButton) {
			return;
		}
		const isStarMode = this.isStarMode();
		this.starModeButton.setAttribute('aria-pressed', isStarMode ? 'true' : 'false');
		this.starModeButton.classList.toggle('is-active', isStarMode);
		this.starModeButton.setAttribute(
			'aria-label',
			isStarMode ? 'Return to land view' : 'Enter Star mode'
		);
		if (this.starModeStatusEl) {
			this.starModeStatusEl.textContent = isStarMode ? 'On' : 'Off';
		}
	};

	App.prototype.enableStarMode = function enableStarMode() {
		if (this.starModeActive) {
			return;
		}
		this.starModeActive = true;
		if (this.baseTileLayer && typeof this.baseTileLayer.setOpacity === 'function') {
			if (this.previousTileOpacity === null) {
				if (typeof this.baseTileLayer.options.opacity === 'number') {
					this.previousTileOpacity = this.baseTileLayer.options.opacity;
				} else if (
					typeof this.baseTileLayer.getOpacity === 'function'
				) {
					this.previousTileOpacity = this.baseTileLayer.getOpacity();
				} else {
					this.previousTileOpacity = this.baseTileOpacity || 1;
				}
			}
			this.baseTileLayer.setOpacity(0.05);
		}
		this.boundaryLayerWasVisible =
			!!(this.map && this.boundaryLayer && this.map.hasLayer(this.boundaryLayer));
		if (this.boundaryLayerWasVisible && this.map && this.boundaryLayer) {
			this.map.removeLayer(this.boundaryLayer);
		}
		this.boundaryHandlesWereVisible =
			!!(
				this.map &&
				this.boundaryControlLayer &&
				this.map.hasLayer(this.boundaryControlLayer)
			);
		if (this.boundaryHandlesWereVisible && this.map && this.boundaryControlLayer) {
			this.map.removeLayer(this.boundaryControlLayer);
		}
		this.hideCityLabelsForStarMode();
		this.showConstellationLines();
		if (typeof this.updateCompactStarDots === 'function') {
			this.updateCompactStarDots();
		}
		if (this.map && typeof this.map.invalidateSize === 'function') {
			this.map.invalidateSize();
		}
	};

	App.prototype.disableStarMode = function disableStarMode() {
		if (!this.starModeActive) {
			return;
		}
		this.starModeActive = false;
		if (this.baseTileLayer && typeof this.baseTileLayer.setOpacity === 'function') {
			const targetOpacity =
				this.previousTileOpacity == null
					? this.baseTileOpacity || 1
					: this.previousTileOpacity;
			this.baseTileLayer.setOpacity(targetOpacity);
		}
		this.previousTileOpacity = null;
		if (this.boundaryLayerWasVisible && this.map && this.boundaryLayer) {
			this.boundaryLayer.addTo(this.map);
		}
		if (this.boundaryHandlesWereVisible && this.map && this.boundaryControlLayer) {
			this.boundaryControlLayer.addTo(this.map);
		}
		this.boundaryLayerWasVisible = false;
		this.boundaryHandlesWereVisible = false;
		this.restoreCityLabelsAfterStarMode();
		this.hideConstellationLines();
		if (typeof this.updateCompactStarDots === 'function') {
			this.updateCompactStarDots();
		}
		if (this.map && typeof this.map.invalidateSize === 'function') {
			this.map.invalidateSize();
		}
	};

	App.prototype.hideCityLabelsForStarMode = function hideCityLabelsForStarMode() {
		if (!Array.isArray(this.cityMarkers)) {
			return;
		}
		for (let i = 0; i < this.cityMarkers.length; i += 1) {
			const entry = this.cityMarkers[i];
			if (entry && typeof this.hideCityTooltip === 'function') {
				this.hideCityTooltip(entry);
			}
			if (entry) {
				entry.lastFontSize = null;
			}
		}
	};

	App.prototype.restoreCityLabelsAfterStarMode = function restoreCityLabelsAfterStarMode() {
		if (typeof this.onZoomChange === 'function') {
			this.onZoomChange();
		}
	};

	App.prototype.showModeIntro = function showModeIntro() {
		if (!this.lorePanelEl) {
			return;
		}
		const html =
			'<p class="info-panel__note"><strong>Edit mode</strong> is active. Click nations or cities to inspect their metadata and drag markers to adjust positions.</p>' +
			'<ul class="info-panel__list">' +
			'<li><kbd>E</kbd> &mdash; switch to edit mode</li>' +
			'<li><kbd>N</kbd> &mdash; return to normal mode</li>' +
			'</ul>';
		this.setPanelContent('Edit mode', html);
	};
}
